import type { ThreeElements } from '@react-three/fiber';
import React, { useMemo, useRef } from 'react';
import {
  color,
  Fn,
  hash,
  instancedArray,
  instanceIndex,
  min,
  mod,
  remap,
  texture,
  time,
  uniform,
  uv,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three';
import { useTexture } from '@react-three/drei';
import { useControls } from 'leva';

const Smoke = ({ ...props }: ThreeElements['sprite']) => {
  const spriteRef = useRef<THREE.Sprite>(null!);
  const spriteMaterialRef = useRef<ThreeElements['spriteMaterial']>(null!);
  const [smokeTexture] = useTexture(['/texture/smoke_03.png']);

  /**-----------------
        Controls
   -------------- */
  const smokeControls = useControls(
    'smoke controls',
    {
      pcount: {
        value: 100,
        min: 1,
        max: 400,
        label: 'count',
      },
      uLife: {
        value: 0.19,
        min: 0.001,
        max: 2,
        step: 0.001,
        onChange: () => {
          spriteMaterialRef.current.needsUpdate = true;
        },
        transient: false,
      },
      uSize: {
        value: 0.32,
        min: 0.001,
        max: 4,
        step: 0.01,
        onChange: () => {
          spriteMaterialRef.current.needsUpdate = true;
        },
        transient: false,
      },
      radius: {
        value: 0.16,
        min: 0.001,
        max: 0.5,
        step: 0.01,
        onChange: () => {
          spriteMaterialRef.current.needsUpdate = true;
        },
        transient: false,
      },
      particleCount: {
        min: 10,
        max: 1000,
        value: 100,
        onChange: () => {
          spriteMaterialRef.current.needsUpdate = true;
        },
        transient: false,
      },
      uTimeCoEfficent: {
        value: 0.06,

        min: 0.01,
        max: 0.5,
        step: 0.001,
        onChange: () => {
          spriteMaterialRef.current.needsUpdate = true;
        },
        transient: false,
      },
    },
    { collapsed: true }
  );

  const utils = useMemo(() => {
    return {
      particleCount: smokeControls.particleCount,
      radius: smokeControls.radius,
    };
  }, [smokeControls.particleCount, smokeControls.radius]);

  /**-------------------
      Nodes and uniforms 
     ---------------------*/
  const { nodes } = useMemo(() => {
    //uniforms
    const uLife = uniform(smokeControls.uLife, 'float');
    const uSize = uniform(smokeControls.uSize, 'float');
    const uTimeCoEfficent = uniform(smokeControls.uTimeCoEfficent, 'float');
    //textures
    const smokeAlphaTexture = texture(smokeTexture, uv()).r;

    //attributes
    const positionArray = new Float32Array(utils.particleCount * 3);
    for (let i = 0; i < utils.particleCount; i++) {
      const shape = new THREE.Cylindrical(
        utils.radius,
        Math.random() * Math.PI * 2,
        0.01
      );

      const position = new THREE.Vector3();
      position.setFromCylindrical(shape);

      const i3 = i * 3;
      positionArray[i3] = position.x;
      positionArray[i3 + 1] = position.y;
      positionArray[i3 + 2] = position.z;
    }

    const positions = instancedArray(positionArray, 'vec3');

    // shader nodes
    const life = mod(time.mul(hash(instanceIndex)).mul(uTimeCoEfficent), uLife);

    const positionNode = Fn(() => {
      const position = positions.element(instanceIndex);

      return vec4(position.x, position.y.add(life), position.z, 0.0);
    })();

    const scaleNode = Fn(() => {
      const sizeOpeningProgress = remap(life, 0.0, 0.1, 0.0, 1.0);
      const sizeClosingProgress = remap(life, 0.1, uLife, 1.0, 0.0);

      const scalingProgress = min(
        sizeOpeningProgress,
        sizeClosingProgress
      ).clamp(0.0, 1.0);
      const randomSize = hash(instanceIndex).mul(0.5);
      const size = uSize.mul(randomSize).mul(scalingProgress);

      return size;
    })();

    const colorNode = Fn(() => {
      const smokeColor = vec3(color('#B1B1B1'));

      return vec4(smokeColor, smokeAlphaTexture);
    });

    //

    return {
      nodes: {
        positionNode,
        colorNode,
        scaleNode,
      },
      uniforms: { uLife, uSize, uTimeCoEfficent },
    };
  }, [
    utils,
    smokeTexture,
    smokeControls.uLife,
    smokeControls.uSize,
    smokeControls.uTimeCoEfficent,
  ]);

  return (
    <>
      <sprite ref={spriteRef} count={utils.particleCount} {...props}>
        <spriteNodeMaterial
          ref={spriteMaterialRef}
          colorNode={nodes.colorNode()}
          positionNode={nodes.positionNode}
          scaleNode={nodes.scaleNode}
          // transparent={true}
          depthWrite={false}
        />
      </sprite>
    </>
  );
};

export default React.memo(Smoke);
